/**
 * EVENT REMINDER MODAL
 * 
 * Lets users set reminders for events.
 * Location: src/components/events/EventReminderModal.tsx
 */

import { useState, useEffect } from 'react';
import { X, Bell, BellOff, Check } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth } from '../../firebase/config';
import type { CalendarEvent, ReminderTiming } from '../../types/event';
import { REMINDER_OPTIONS } from '../../types/event';
import {
  setEventReminder,
  removeEventReminder,
  getEventReminders
} from '../../services/eventReminderService';

interface EventReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent;
}

export default function EventReminderModal({
  isOpen,
  onClose,
  event
}: EventReminderModalProps) {
  const [user, setUser] = useState<User | null>(null);
  const [activeReminders, setActiveReminders] = useState<ReminderTiming[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<ReminderTiming | null>(null);

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  // Load existing reminders
  useEffect(() => {
    async function loadReminders() {
      if (!user || !event.id || !isOpen) return;
      
      setLoading(true);
      try {
        const reminders = await getEventReminders(user.uid, event.id);
        setActiveReminders(reminders);
      } catch (err) {
        console.error('Error loading reminders:', err);
      } finally {
        setLoading(false);
      }
    }
    
    loadReminders();
  }, [user, event.id, isOpen]);

  // Toggle a reminder
  const toggleReminder = async (timing: ReminderTiming) => {
    if (!user || !event.id) return;
    
    setSaving(timing);
    try {
      if (activeReminders.includes(timing)) {
        await removeEventReminder(user.uid, event.id, timing);
        setActiveReminders(activeReminders.filter(t => t !== timing));
      } else {
        await setEventReminder(user.uid, event, timing);
        setActiveReminders([...activeReminders, timing]);
      }
    } catch (err) {
      console.error('Error toggling reminder:', err);
    } finally {
      setSaving(null);
    }
  };

  if (!isOpen) return null;

  const eventDate = event.startDate.toDate();
  const now = new Date();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md glass-card p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
            <Bell className="icon-base text-purple-400" />
            Set Reminders
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-default"
          >
            <X className="icon-base text-glass-secondary" />
          </button>
        </div>

        {/* Event info */}
        <div className="bg-slate-800/60 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-glass">{event.title}</h3>
          <p className="text-sm text-glass-secondary">
            {event.brandName} • {eventDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
            {event.startTime && ` at ${event.startTime}`}
          </p>
        </div>

        {/* Not logged in */}
        {!user && (
          <div className="text-center py-6">
            <BellOff className="icon-lg text-glass-muted mx-auto mb-3" />
            <p className="text-glass-secondary mb-4">
              Sign in to set reminders for this event
            </p>
            <a href="/auth" className="btn-primary inline-block">
              Sign In
            </a>
          </div>
        )}

        {/* Loading */}
        {user && loading && (
          <div className="text-center py-6">
            <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-glass-secondary text-sm">Loading reminders...</p>
          </div>
        )}

        {/* Reminder options */}
        {user && !loading && (
          <div className="space-y-3">
            <p className="text-sm text-glass-secondary mb-4">
              Get notified before this event:
            </p>
            
            {(Object.entries(REMINDER_OPTIONS) as [ReminderTiming, typeof REMINDER_OPTIONS[ReminderTiming]][]).map(([timing, config]) => {
              const isActive = activeReminders.includes(timing);
              const isSaving = saving === timing;
              const reminderDate = new Date(eventDate.getTime() - config.milliseconds);
              const isPast = reminderDate < now;
              
              return (
                <button
                  key={timing}
                  onClick={() => toggleReminder(timing)}
                  disabled={isPast || isSaving}
                  className={`w-full flex items-center justify-between p-4 rounded-lg transition-default ${
                    isPast
                      ? 'bg-slate-800/40 opacity-50 cursor-not-allowed'
                      : isActive
                        ? 'bg-purple-500/20 border border-purple-500/50'
                        : 'bg-slate-800/60 hover:bg-slate-700/60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-purple-500' : 'bg-slate-700'
                    }`}>
                      {isSaving ? (
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : isActive ? (
                        <Check className="icon-sm text-white" />
                      ) : (
                        <Bell className="icon-sm text-glass-secondary" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-glass">{config.label}</p>
                      {isPast ? (
                        <p className="text-xs text-glass-muted">This time has passed</p>
                      ) : (
                        <p className="text-xs text-glass-secondary">
                          {reminderDate.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                          {timing !== '1week' && ` at ${reminderDate.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}`}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {isActive && !isPast && (
                    <span className="text-xs text-purple-400">Active</span>
                  )}
                </button>
              );
            })}
            
            <p className="text-xs text-glass-muted text-center mt-4">
              You'll receive a push notification at these times
            </p>
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="btn-secondary w-full mt-6"
        >
          Done
        </button>
      </div>
    </div>
  );
}
